/**
 * Flow diagram — one setup step feeding a live service, which two audiences consume.
 * Drawn in SVG. Horizontal on desktop, vertical on mobile (separate geometry so text
 * stays legible rather than scaling into illegibility).
 *
 * Accent #6366f1 validated against the dark surface (#0f172a).
 */

const ACCENT = '#6366f1';
const ACCENT_DIM = 'rgba(99,102,241,0.35)';
const STROKE = '#334155';
const SURFACE = 'rgba(30,41,59,0.5)';
const INK = '#f1f5f9';
const INK_MUTED = '#94a3b8';
const INK_FAINT = '#64748b';

function Defs() {
  return (
    <defs>
      <marker
        id="flow-arrow"
        viewBox="0 0 10 10"
        refX="9"
        refY="5"
        markerWidth="6"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" fill={ACCENT_DIM} />
      </marker>
    </defs>
  );
}

/* ------------------------------------------------------------------ desktop */

function DesktopFlow() {
  return (
    <svg
      viewBox="0 0 900 300"
      className="hidden sm:block w-full h-auto"
      role="img"
      aria-label="Create your service, which runs live, and is then reached by customers in conversation and by external AI agents over MCP"
    >
      <Defs />

      {/* connectors first, so nodes sit on top */}
      <path
        d="M 250 150 L 292 150"
        stroke={ACCENT_DIM}
        strokeWidth="2"
        fill="none"
        markerEnd="url(#flow-arrow)"
      />
      <path
        d="M 500 130 C 545 130, 545 78, 588 78"
        stroke={ACCENT_DIM}
        strokeWidth="2"
        fill="none"
        markerEnd="url(#flow-arrow)"
      />
      <path
        d="M 500 170 C 545 170, 545 222, 588 222"
        stroke={ACCENT_DIM}
        strokeWidth="2"
        fill="none"
        markerEnd="url(#flow-arrow)"
      />

      {/* 01 — create */}
      <g>
        <rect x="40" y="105" width="210" height="90" rx="14" fill={SURFACE} stroke={STROKE} />
        <text x="60" y="132" fontSize="11" fontFamily="ui-monospace, monospace" fill={ACCENT}>
          01
        </text>
        <text x="60" y="155" fontSize="15" fontWeight="600" fill={INK}>
          Create your service
        </text>
        <text x="60" y="176" fontSize="12" fill={INK_MUTED}>
          Pick a type. Add your content.
        </text>
      </g>

      {/* live service — the hub */}
      <g>
        <rect
          x="292"
          y="105"
          width="208"
          height="90"
          rx="14"
          fill="rgba(99,102,241,0.10)"
          stroke={ACCENT}
          strokeWidth="1.5"
        />
        <circle cx="316" cy="129" r="4" fill={ACCENT} />
        <text x="330" y="133" fontSize="11" fill={ACCENT}>
          live
        </text>
        <text x="312" y="158" fontSize="15" fontWeight="600" fill={INK}>
          Your service
        </text>
        <text x="312" y="179" fontSize="12" fill={INK_MUTED}>
          Grounded in your own content
        </text>
      </g>

      {/* 02 — customers */}
      <g>
        <rect x="588" y="33" width="272" height="90" rx="14" fill={SURFACE} stroke={STROKE} />
        <text x="608" y="60" fontSize="11" fontFamily="ui-monospace, monospace" fill={ACCENT}>
          02
        </text>
        <text x="608" y="83" fontSize="15" fontWeight="600" fill={INK}>
          Customers converse
        </text>
        <text x="608" y="104" fontSize="12" fill={INK_MUTED}>
          They ask, book, order — not just chat.
        </text>
      </g>

      {/* 03 — agents */}
      <g>
        <rect x="588" y="177" width="272" height="90" rx="14" fill={SURFACE} stroke={STROKE} />
        <text x="608" y="204" fontSize="11" fontFamily="ui-monospace, monospace" fill={ACCENT}>
          03
        </text>
        <text x="608" y="227" fontSize="15" fontWeight="600" fill={INK}>
          AI agents connect
        </text>
        <text x="608" y="248" fontSize="12" fill={INK_MUTED}>
          External assistants reach it over MCP.
        </text>
      </g>

      {/* branch annotation */}
      <text x="544" y="155" fontSize="10.5" fill={INK_FAINT} textAnchor="middle">
        two audiences
      </text>
      <text x="544" y="168" fontSize="10.5" fill={INK_FAINT} textAnchor="middle">
        one service
      </text>
    </svg>
  );
}

/* ------------------------------------------------------------------- mobile */

function MobileFlow() {
  const branches = [
    { n: '02', title: 'Customers converse', detail: 'They ask, book, order.', y: 268 },
    { n: '03', title: 'AI agents connect', detail: 'External assistants, over MCP.', y: 388 },
  ];

  return (
    <svg
      viewBox="0 0 340 510"
      className="sm:hidden w-full h-auto"
      role="img"
      aria-label="Create your service, which runs live, and is then reached by customers in conversation and by external AI agents over MCP"
    >
      <Defs />

      {/* 01 -> hub */}
      <path d="M 170 110 L 170 124" stroke={ACCENT_DIM} strokeWidth="2" markerEnd="url(#flow-arrow)" />

      {/* spine down the left, branches right into each audience */}
      <path
        d="M 34 230 L 34 438"
        stroke={ACCENT_DIM}
        strokeWidth="2"
        fill="none"
      />
      {branches.map((b) => (
        <path
          key={b.n}
          d={`M 34 ${b.y + 50} L 54 ${b.y + 50}`}
          stroke={ACCENT_DIM}
          strokeWidth="2"
          fill="none"
          markerEnd="url(#flow-arrow)"
        />
      ))}

      {/* 01 */}
      <rect x="16" y="10" width="308" height="100" rx="14" fill={SURFACE} stroke={STROKE} />
      <text x="34" y="40" fontSize="13" fontFamily="ui-monospace, monospace" fill={ACCENT}>
        01
      </text>
      <text x="34" y="68" fontSize="16" fontWeight="600" fill={INK}>
        Create your service
      </text>
      <text x="34" y="90" fontSize="13.5" fill={INK_MUTED}>
        Pick a type. Add your content.
      </text>

      {/* hub */}
      <rect
        x="16"
        y="130"
        width="308"
        height="100"
        rx="14"
        fill="rgba(99,102,241,0.10)"
        stroke={ACCENT}
        strokeWidth="1.5"
      />
      <circle cx="38" cy="156" r="4" fill={ACCENT} />
      <text x="52" y="160" fontSize="13" fill={ACCENT}>
        live
      </text>
      <text x="34" y="188" fontSize="16" fontWeight="600" fill={INK}>
        Your service
      </text>
      <text x="34" y="210" fontSize="13.5" fill={INK_MUTED}>
        Grounded in your own content
      </text>

      <text x="44" y="252" fontSize="11" fill={INK_FAINT}>
        two audiences, one service
      </text>

      {/* branches */}
      {branches.map((b) => (
        <g key={b.title}>
          <rect x="56" y={b.y} width="268" height="100" rx="14" fill={SURFACE} stroke={STROKE} />
          <text x="74" y={b.y + 30} fontSize="13" fontFamily="ui-monospace, monospace" fill={ACCENT}>
            {b.n}
          </text>
          <text x="74" y={b.y + 58} fontSize="16" fontWeight="600" fill={INK}>
            {b.title}
          </text>
          <text x="74" y={b.y + 80} fontSize="13.5" fill={INK_MUTED}>
            {b.detail}
          </text>
        </g>
      ))}
    </svg>
  );
}

export function HowItWorksFlow() {
  return (
    <figure className="m-0">
      <DesktopFlow />
      <MobileFlow />
      <figcaption className="text-[11px] text-slate-500 leading-relaxed mt-4 text-center">
        Steps 02 and 03 are the same service reached two ways — a person in conversation, or their
        assistant over MCP.
      </figcaption>
    </figure>
  );
}
